/**
 * Environment Configuration
 * Centralized configuration management for different environments
 */

interface EnvironmentConfig {
  // API Configuration
  apiBaseUrl: string;
  apiTimeout: number;

  // Environment
  environment: 'development' | 'staging' | 'production' | 'test';
  nodeEnv: string;

  // Debug Settings
  debug: boolean;
  enableLogging: boolean;
  logLevel: 'debug' | 'info' | 'warn' | 'error';

  // Mock API
  mockApi: boolean;

  // Development Features
  enableDevTools: boolean;
  enableHotReload: boolean;

  // Security Settings
  enableHttps: boolean;
  strictMode: boolean;

  // Performance Settings
  enableCache: boolean;
  cacheDuration: number;

  // UI Settings
  showPerformanceMetrics: boolean;
  enableErrorOverlay: boolean;

  // Authentication
  tokenStorage: 'localStorage' | 'sessionStorage';
  sessionTimeout: number;

  // Internationalization
  defaultLanguage: string;
  fallbackLanguage: string;
  enableRtl: boolean;

  // File Upload
  maxFileSize: number;
  allowedFileTypes: string[];

  // Pagination
  defaultPageSize: number;
  maxPageSize: number;

  // Analytics & Monitoring
  enableAnalytics: boolean;
  sentryDsn?: string;

  // CDN Configuration
  cdnUrl?: string;
  staticAssetsUrl?: string;
}

/**
 * Get environment variable with type safety
 */
const getEnvVar = (key: string, fallback: string = ''): string => {
  const value = import.meta.env[key];
  if (value === undefined && fallback === '') {
    console.warn(`Environment variable ${key} is not defined`);
  }
  return value || fallback;
};

/**
 * Get boolean environment variable
 */
const getBooleanEnvVar = (key: string, fallback: boolean = false): boolean => {
  const value = getEnvVar(key);
  if (value === '') return fallback;
  return value.toLowerCase() === 'true' || value === '1';
};

/**
 * Get number environment variable
 */
const getNumberEnvVar = (key: string, fallback: number = 0): number => {
  const value = getEnvVar(key);
  if (value === '') return fallback;
  const parsed = parseInt(value, 10);
  return isNaN(parsed) ? fallback : parsed;
};

/**
 * Environment configuration object
 */
export const env: EnvironmentConfig = {
  // API Configuration
  apiBaseUrl: getEnvVar('VITE_APP_API_BASE_URL', 'https://localhost:7257/api'),
  apiTimeout: getNumberEnvVar('VITE_APP_API_TIMEOUT', 30000),

  // Environment
  environment: getEnvVar('VITE_APP_ENV', 'development') as EnvironmentConfig['environment'],
  nodeEnv: getEnvVar('VITE_APP_NODE_ENV', 'development'),

  // Debug Settings
  debug: getBooleanEnvVar('VITE_APP_DEBUG', true),
  enableLogging: getBooleanEnvVar('VITE_APP_ENABLE_LOGGING', true),
  logLevel: getEnvVar('VITE_APP_LOG_LEVEL', 'debug') as EnvironmentConfig['logLevel'],

  // Mock API
  mockApi: getBooleanEnvVar('VITE_APP_MOCK_API', false),

  // Development Features
  enableDevTools: getBooleanEnvVar('VITE_APP_ENABLE_DEV_TOOLS', true),
  enableHotReload: getBooleanEnvVar('VITE_APP_ENABLE_HOT_RELOAD', true),

  // Security Settings
  enableHttps: getBooleanEnvVar('VITE_APP_ENABLE_HTTPS', false),
  strictMode: getBooleanEnvVar('VITE_APP_STRICT_MODE', false),

  // Performance Settings
  enableCache: getBooleanEnvVar('VITE_APP_ENABLE_CACHE', false),
  cacheDuration: getNumberEnvVar('VITE_APP_CACHE_DURATION', 0),

  // UI Settings
  showPerformanceMetrics: getBooleanEnvVar('VITE_APP_SHOW_PERFORMANCE_METRICS', false),
  enableErrorOverlay: getBooleanEnvVar('VITE_APP_ENABLE_ERROR_OVERLAY', true),

  // Authentication
  tokenStorage: getEnvVar('VITE_APP_TOKEN_STORAGE', 'localStorage') as EnvironmentConfig['tokenStorage'],
  sessionTimeout: getNumberEnvVar('VITE_APP_SESSION_TIMEOUT', 3600000),

  // Internationalization
  defaultLanguage: getEnvVar('VITE_APP_DEFAULT_LANGUAGE', 'en'),
  fallbackLanguage: getEnvVar('VITE_APP_FALLBACK_LANGUAGE', 'en'),
  enableRtl: getBooleanEnvVar('VITE_APP_ENABLE_RTL', true),

  // File Upload
  maxFileSize: getNumberEnvVar('VITE_APP_MAX_FILE_SIZE', 10485760), // 10MB
  allowedFileTypes: getEnvVar('VITE_APP_ALLOWED_FILE_TYPES', 'jpg,jpeg,png,pdf').split(','),

  // Pagination
  defaultPageSize: getNumberEnvVar('VITE_APP_DEFAULT_PAGE_SIZE', 10),
  maxPageSize: getNumberEnvVar('VITE_APP_MAX_PAGE_SIZE', 100),

  // Analytics & Monitoring
  enableAnalytics: getBooleanEnvVar('VITE_APP_ENABLE_ANALYTICS', false),
  sentryDsn: getEnvVar('VITE_APP_SENTRY_DSN'),

  // CDN Configuration
  cdnUrl: getEnvVar('VITE_APP_CDN_URL'),
  staticAssetsUrl: getEnvVar('VITE_APP_STATIC_ASSETS_URL'),
};

/**
 * Helper functions for common environment checks
 */
export const isDevelopment = () => env.environment === 'development';
export const isProduction = () => env.environment === 'production';
export const isStaging = () => env.environment === 'staging';
export const isTest = () => env.environment === 'test';

/**
 * Get API URL with endpoint
 */
export const getApiUrl = (endpoint: string = '') => {
  const baseUrl = env.apiBaseUrl.endsWith('/')
    ? env.apiBaseUrl.slice(0, -1)
    : env.apiBaseUrl;

  const cleanEndpoint = endpoint.startsWith('/')
    ? endpoint
    : `/${endpoint}`;

  return `${baseUrl}${cleanEndpoint}`;
};

/**
 * Get static asset URL
 */
export const getAssetUrl = (path: string): string => {
  if (!env.staticAssetsUrl) return path;
  const baseUrl = env.staticAssetsUrl.endsWith('/')
    ? env.staticAssetsUrl.slice(0, -1)
    : env.staticAssetsUrl;
  const cleanPath = path.startsWith('/') ? path : `/${path}`;
  return `${baseUrl}${cleanPath}`;
};

/**
 * Logging helper that respects environment settings
 */
export const log = {
  debug: (...args: any[]) => {
    if (env.debug && env.enableLogging && ['debug'].includes(env.logLevel)) {
      console.log('[DEBUG]', ...args);
    }
  },
  info: (...args: any[]) => {
    if (env.enableLogging && ['debug', 'info'].includes(env.logLevel)) {
      console.info('[INFO]', ...args);
    }
  },
  warn: (...args: any[]) => {
    if (env.enableLogging && ['debug', 'info', 'warn'].includes(env.logLevel)) {
      console.warn('[WARN]', ...args);
    }
  },
  error: (...args: any[]) => {
    if (env.enableLogging) {
      console.error('[ERROR]', ...args);
    }
  }
};

/**
 * Export environment info for debugging
 */
if (isDevelopment()) {
  console.log('🔧 Environment Configuration:', {
    environment: env.environment,
    nodeEnv: env.nodeEnv,
    apiBaseUrl: env.apiBaseUrl,
    apiTimeout: env.apiTimeout,
    debug: env.debug,
    enableLogging: env.enableLogging,
    logLevel: env.logLevel,
    mockApi: env.mockApi,
    enableDevTools: env.enableDevTools,
    enableCache: env.enableCache,
    defaultLanguage: env.defaultLanguage,
    enableRtl: env.enableRtl,
  });
}

export default env;
